import { useState, useMemo } from 'react';

type TimeRange = '7d' | '30d' | '90d' | 'custom';
type ActiveTab = 'overview' | 'production' | 'safety' | 'facility' | 'environment';

interface DateRange {
  start: string;
  end: string;
}

const mockDailyData = [
  { date: '01-09', flights: 18, tasks: 12, alerts: 3, flightHours: 8.5, production: 92, safety: 88, facility: 95, environment: 78 },
  { date: '01-10', flights: 22, tasks: 15, alerts: 2, flightHours: 10.2, production: 95, safety: 91, facility: 90, environment: 82 },
  { date: '01-11', flights: 16, tasks: 10, alerts: 5, flightHours: 7.8, production: 88, safety: 75, facility: 88, environment: 70 },
  { date: '01-12', flights: 25, tasks: 18, alerts: 1, flightHours: 12.0, production: 97, safety: 96, facility: 93, environment: 89 },
  { date: '01-13', flights: 20, tasks: 14, alerts: 4, flightHours: 9.5, production: 90, safety: 82, facility: 91, environment: 75 },
  { date: '01-14', flights: 28, tasks: 20, alerts: 2, flightHours: 13.5, production: 98, safety: 94, facility: 96, environment: 91 },
  { date: '01-15', flights: 24, tasks: 17, alerts: 3, flightHours: 11.0, production: 94, safety: 89, facility: 92, environment: 85 },
];

const moduleStats = [
  { module: '生产运营', tasks: 186, completed: 178, alerts: 12, efficiency: 95.7, trend: '+3.2%', color: 'teal' },
  { module: '安全应急', tasks: 142, completed: 138, alerts: 28, efficiency: 97.2, trend: '+1.8%', color: 'green' },
  { module: '设施设备', tasks: 98, completed: 94, alerts: 8, efficiency: 95.9, trend: '+2.5%', color: 'orange' },
  { module: '环境保护', tasks: 76, completed: 71, alerts: 15, efficiency: 93.4, trend: '-0.6%', color: 'red' },
];

const droneUsageData = [
  { name: 'UAV001', totalFlights: 48, totalHours: 22.5, taskTypes: '油污监测、安防巡逻', status: '良好' },
  { name: 'UAV002', totalFlights: 52, totalHours: 24.8, taskTypes: '粉尘监控、设施巡检', status: '良好' },
  { name: 'UAV003', totalFlights: 35, totalHours: 16.2, taskTypes: '船舶辅助、水尺计重', status: '维护中' },
  { name: 'UAV004', totalFlights: 41, totalHours: 19.0, taskTypes: '航道巡检、三维建模', status: '良好' },
  { name: 'UAV005', totalFlights: 38, totalHours: 17.5, taskTypes: '高架桥巡检、管道巡检', status: '良好' },
  { name: 'UAV006', totalFlights: 44, totalHours: 20.3, taskTypes: '夜间侦察、安防巡逻', status: '良好' },
];

const alertDistribution = [
  { type: '环境预警', count: 27, percentage: 35, color: 'bg-teal-400' },
  { type: '安全预警', count: 21, percentage: 27, color: 'bg-orange-400' },
  { type: '设施预警', count: 18, percentage: 23, color: 'bg-red-400' },
  { type: '生产预警', count: 12, percentage: 15, color: 'bg-green-400' },
];

const productionDetails = [
  { item: '散货堆场盘点', count: 48, avgDuration: '1.8h', accuracy: '99.2%', savings: '节省72人工时' },
  { item: '船舶进出港辅助', count: 36, avgDuration: '2.5h', accuracy: '100%', savings: '提效40%' },
  { item: '水尺计重作业', count: 28, avgDuration: '0.8h', accuracy: '98.7%', savings: '节省56人工时' },
  { item: '航道航标巡检', count: 42, avgDuration: '3.2h', accuracy: '99.5%', savings: '节省126人工时' },
];

const safetyDetails = [
  { item: '全天候安防巡逻', patrols: 186, violations: 14, responseTime: '3.2min', coverage: '98%' },
  { item: '突发事件应急', incidents: 8, resolved: 8, avgResponseTime: '8.5min', successRate: '100%' },
  { item: '夜间侦察任务', patrols: 62, anomalies: 5, detectionRate: '100%', falseAlarm: '2%' },
  { item: '三维建模更新', models: 12, coverage: '全港区', accuracy: '±5cm', updateFreq: '每周' },
];

export default function DataReport() {
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [customDate, setCustomDate] = useState<DateRange>({ start: '2025-01-01', end: '2025-01-15' });
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const summaryStats = useMemo(() => ({
    totalFlights: mockDailyData.reduce((s, d) => s + d.flights, 0),
    totalTasks: mockDailyData.reduce((s, d) => s + d.tasks, 0),
    totalAlerts: mockDailyData.reduce((s, d) => s + d.alerts, 0),
    totalHours: mockDailyData.reduce((s, d) => s + d.flightHours, 0).toFixed(1),
  }), []);

  const maxFlights = Math.max(...mockDailyData.map(d => d.flights));

  const handleExport = () => {
    setExportLoading(true);
    setTimeout(() => {
      setExportLoading(false);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 2500);
    }, 1500);
  };

  const tabs: { key: ActiveTab; label: string; icon: string }[] = [
    { key: 'overview', label: '总览', icon: 'ri-pie-chart-line' },
    { key: 'production', label: '生产运营', icon: 'ri-ship-line' },
    { key: 'safety', label: '安全应急', icon: 'ri-shield-check-line' },
    { key: 'facility', label: '设施设备', icon: 'ri-building-line' },
    { key: 'environment', label: '环境保护', icon: 'ri-leaf-line' },
  ];

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">数据统计报表</h2>
          <p className="text-gray-400 text-sm mt-1">各模块运营数据汇总分析</p>
        </div>
        <div className="flex items-center gap-3">
          {/* 时间范围选择 */}
          <div className="flex items-center bg-gray-800 rounded-lg p-1 border border-teal-500/20">
            {(['7d', '30d', '90d'] as TimeRange[]).map((r) => (
              <button
                key={r}
                onClick={() => { setTimeRange(r); setShowCustomPicker(false); }}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  timeRange === r ? 'bg-teal-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {r === '7d' ? '近7天' : r === '30d' ? '近30天' : '近90天'}
              </button>
            ))}
            <button
              onClick={() => { setTimeRange('custom'); setShowCustomPicker(!showCustomPicker); }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                timeRange === 'custom' ? 'bg-teal-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              自定义
            </button>
          </div>

          {/* 自定义日期选择器 */}
          {showCustomPicker && (
            <div className="flex items-center gap-2 bg-gray-800 border border-teal-500/20 rounded-lg px-3 py-2">
              <input
                type="date"
                value={customDate.start}
                onChange={(e) => setCustomDate({ ...customDate, start: e.target.value })}
                className="bg-transparent text-white text-sm outline-none cursor-pointer"
              />
              <span className="text-gray-400 text-sm">至</span>
              <input
                type="date"
                value={customDate.end}
                onChange={(e) => setCustomDate({ ...customDate, end: e.target.value })}
                className="bg-transparent text-white text-sm outline-none cursor-pointer"
              />
              <button
                onClick={() => setShowCustomPicker(false)}
                className="px-3 py-1 bg-teal-500 text-white text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-teal-600 transition-colors"
              >
                确定
              </button>
            </div>
          )}

          {/* 导出按钮 */}
          <button
            onClick={handleExport}
            disabled={exportLoading}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {exportLoading ? (
              <><i className="ri-loader-4-line animate-spin"></i>导出中...</>
            ) : exportSuccess ? (
              <><i className="ri-check-line"></i>导出成功</>
            ) : (
              <><i className="ri-download-2-line"></i>导出报表</>
            )}
          </button>
        </div>
      </div>

      {/* 当前时间范围提示 */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <i className="ri-calendar-line text-teal-400"></i>
        <span>
          统计周期：
          {timeRange === '7d' && '2025-01-09 ~ 2025-01-15（近7天）'}
          {timeRange === '30d' && '2024-12-16 ~ 2025-01-15（近30天）'}
          {timeRange === '90d' && '2024-10-17 ~ 2025-01-15（近90天）'}
          {timeRange === 'custom' && `${customDate.start} ~ ${customDate.end}（自定义）`}
        </span>
      </div>

      {/* 核心汇总卡片 */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: '总飞行架次', value: summaryStats.totalFlights, unit: '次', icon: 'ri-flight-takeoff-line', color: 'teal', sub: '较上期 +12.4%', up: true },
          { label: '完成任务数', value: summaryStats.totalTasks, unit: '项', icon: 'ri-task-line', color: 'green', sub: '完成率 96.2%', up: true },
          { label: '预警事件数', value: summaryStats.totalAlerts, unit: '次', icon: 'ri-alarm-warning-line', color: 'orange', sub: '较上期 -8.3%', up: false },
          { label: '累计飞行时长', value: summaryStats.totalHours, unit: 'h', icon: 'ri-time-line', color: 'sky', sub: '较上期 +15.6%', up: true },
        ].map((card) => (
          <div key={card.label} className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-5 border border-teal-500/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">{card.label}</span>
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-${card.color}-500/10`}>
                <i className={`${card.icon} text-xl text-${card.color}-400`}></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {card.value}<span className="text-base font-normal text-gray-400 ml-1">{card.unit}</span>
            </div>
            <div className={`text-xs flex items-center gap-1 ${card.up ? 'text-teal-400' : 'text-orange-400'}`}>
              <i className={`ri-arrow-${card.up ? 'up' : 'down'}-line`}></i>
              {card.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Tab 切换 */}
      <div className="flex items-center gap-1 bg-gray-800/50 rounded-lg p-1 border border-teal-500/20 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-teal-500 text-white shadow'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== 总览 Tab ===== */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* 飞行架次趋势图（纯CSS柱状图） */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-2-line text-teal-400"></i>
              每日飞行架次趋势
            </h3>
            <div className="flex items-end gap-3 h-48">
              {mockDailyData.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs text-teal-400 font-medium">{d.flights}</span>
                  <div className="w-full flex flex-col justify-end" style={{ height: '160px' }}>
                    <div
                      className="w-full bg-gradient-to-t from-teal-600 to-teal-400 rounded-t-md transition-all duration-500 hover:from-teal-500 hover:to-teal-300 cursor-pointer"
                      style={{ height: `${(d.flights / maxFlights) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400">{d.date}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 各模块综合评分 + 预警分布 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 各模块综合评分 */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6 border border-teal-500/20">
              <h3 className="text-base font-semibold text-white mb-5 flex items-center gap-2">
                <i className="ri-radar-line text-teal-400"></i>
                各模块运营效率
              </h3>
              <div className="space-y-4">
                {moduleStats.map((m) => (
                  <div key={m.module}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-300">{m.module}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${m.trend.startsWith('+') ? 'text-teal-400' : 'text-red-400'}`}>{m.trend}</span>
                        <span className="text-sm font-semibold text-white">{m.efficiency}%</span>
                      </div>
                    </div>
                    <div className="w-full h-2.5 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          m.color === 'teal' ? 'bg-teal-400' :
                          m.color === 'green' ? 'bg-green-400' :
                          m.color === 'orange' ? 'bg-orange-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${m.efficiency}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-gray-500">任务 {m.tasks} 项</span>
                      <span className="text-xs text-gray-500">完成 {m.completed} 项</span>
                      <span className="text-xs text-orange-400">预警 {m.alerts} 次</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 预警分布 */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6 border border-teal-500/20">
              <h3 className="text-base font-semibold text-white mb-5 flex items-center gap-2">
                <i className="ri-pie-chart-line text-teal-400"></i>
                预警事件分布
              </h3>
              <div className="space-y-4">
                {alertDistribution.map((a) => (
                  <div key={a.type} className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${a.color} flex-shrink-0`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-300">{a.type}</span>
                        <span className="text-sm font-semibold text-white">{a.count} 次</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${a.color}`}
                          style={{ width: `${a.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 w-8 text-right">{a.percentage}%</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-700 flex items-center justify-between">
                <span className="text-sm text-gray-400">预警总计</span>
                <span className="text-xl font-bold text-white">78 <span className="text-sm font-normal text-gray-400">次</span></span>
              </div>
            </div>
          </div>

          {/* 无人机使用统计 */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <i className="ri-flight-takeoff-line text-teal-400"></i>
              无人机使用统计
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    {['设备编号', '飞行架次', '飞行时长', '主要任务类型', '设备状态'].map((h) => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {droneUsageData.map((d) => (
                    <tr key={d.name} className="border-b border-gray-700/40 hover:bg-gray-700/20 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-teal-400">{d.name}</td>
                      <td className="py-3 px-4 text-sm text-white">{d.totalFlights} 次</td>
                      <td className="py-3 px-4 text-sm text-white">{d.totalHours} h</td>
                      <td className="py-3 px-4 text-sm text-gray-300">{d.taskTypes}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          d.status === '良好' ? 'bg-teal-500/20 text-teal-400' : 'bg-orange-500/20 text-orange-400'
                        }`}>{d.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== 生产运营 Tab ===== */}
      {activeTab === 'production' && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: '盘垛作业次数', value: '48', unit: '次', icon: 'ri-stack-line', color: 'teal' },
              { label: '船舶辅助靠泊', value: '36', unit: '艘次', icon: 'ri-ship-line', color: 'green' },
              { label: '水尺计重作业', value: '28', unit: '次', icon: 'ri-scales-3-line', color: 'orange' },
              { label: '航道巡检里程', value: '1,248', unit: 'km', icon: 'ri-route-line', color: 'sky' },
            ].map((c) => (
              <div key={c.label} className="bg-gray-800/50 rounded-lg p-5 border border-teal-500/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-400">{c.label}</span>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-${c.color}-500/10`}>
                    <i className={`${c.icon} text-xl text-${c.color}-400`}></i>
                  </div>
                </div>
                <div className="text-3xl font-bold text-white">{c.value}<span className="text-sm font-normal text-gray-400 ml-1">{c.unit}</span></div>
              </div>
            ))}
          </div>

          {/* 生产运营每日趋势 */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-6 flex items-center gap-2">
              <i className="ri-line-chart-line text-teal-400"></i>
              生产运营效率趋势（%）
            </h3>
            <div className="flex items-end gap-3 h-48">
              {mockDailyData.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs text-green-400 font-medium">{d.production}%</span>
                  <div className="w-full flex flex-col justify-end" style={{ height: '160px' }}>
                    <div
                      className="w-full bg-gradient-to-t from-green-700 to-green-400 rounded-t-md"
                      style={{ height: `${d.production}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400">{d.date}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 生产运营详细数据 */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <i className="ri-table-line text-teal-400"></i>
              生产运营子项统计
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    {['作业项目', '作业次数', '平均时长', '精度/准确率', '效益说明'].map((h) => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-medium text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {productionDetails.map((row) => (
                    <tr key={row.item} className="border-b border-gray-700/40 hover:bg-gray-700/20 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-white">{row.item}</td>
                      <td className="py-3 px-4 text-sm text-teal-400 font-semibold">{row.count} 次</td>
                      <td className="py-3 px-4 text-sm text-gray-300">{row.avgDuration}</td>
                      <td className="py-3 px-4 text-sm text-green-400">{row.accuracy}</td>
                      <td className="py-3 px-4 text-sm text-gray-300">{row.savings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== 安全应急 Tab ===== */}
      {activeTab === 'safety' && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: '安防巡逻次数', value: '186', unit: '次', icon: 'ri-shield-check-line', color: 'teal' },
              { label: '违规行为识别', value: '14', unit: '起', icon: 'ri-error-warning-line', color: 'orange' },
              { label: '应急响应次数', value: '8', unit: '次', icon: 'ri-alarm-warning-line', color: 'red' },
              { label: '平均响应时间', value: '3.2', unit: 'min', icon: 'ri-time-line', color: 'green' },
            ].map((c) => (
              <div key={c.label} className="bg-gray-800/50 rounded-lg p-5 border border-teal-500/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-400">{c.label}</span>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-${c.color}-500/10`}>
                    <i className={`${c.icon} text-xl text-${c.color}-400`}></i>
                  </div>
                </div>
                <div className="text-3xl font-bold text-white">{c.value}<span className="text-sm font-normal text-gray-400 ml-1">{c.unit}</span></div>
              </div>
            ))}
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-line text-teal-400"></i>
              安全指数趋势（%）
            </h3>
            <div className="flex items-end gap-3 h-48">
              {mockDailyData.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs text-orange-400 font-medium">{d.safety}%</span>
                  <div className="w-full flex flex-col justify-end" style={{ height: '160px' }}>
                    <div
                      className="w-full bg-gradient-to-t from-orange-700 to-orange-400 rounded-t-md"
                      style={{ height: `${d.safety}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400">{d.date}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <i className="ri-table-line text-teal-400"></i>
              安全应急子项统计
            </h3>
            <div className="space-y-4">
              {safetyDetails.map((row, i) => (
                <div key={i} className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/30">
                  <div className="font-medium text-white mb-3">{row.item}</div>
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(row).filter(([k]) => k !== 'item').map(([k, v]) => (
                      <div key={k}>
                        <div className="text-xs text-gray-400 mb-1">{
                          k === 'patrols' ? '巡逻次数' :
                          k === 'violations' ? '违规识别' :
                          k === 'responseTime' ? '响应时间' :
                          k === 'coverage' ? '覆盖率' :
                          k === 'incidents' ? '事件数' :
                          k === 'resolved' ? '已处置' :
                          k === 'avgResponseTime' ? '平均响应' :
                          k === 'successRate' ? '处置成功率' :
                          k === 'anomalies' ? '异常发现' :
                          k === 'detectionRate' ? '检出率' :
                          k === 'falseAlarm' ? '误报率' :
                          k === 'models' ? '建模次数' :
                          k === 'accuracy' ? '精度' :
                          k === 'updateFreq' ? '更新频率' : k
                        }</div>
                        <div className="text-sm font-semibold text-teal-400">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== 设施设备 Tab ===== */}
      {activeTab === 'facility' && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: '巡检设施总数', value: '38', unit: '处', icon: 'ri-building-line', color: 'teal' },
              { label: '发现隐患数', value: '12', unit: '处', icon: 'ri-alert-line', color: 'orange' },
              { label: '已整改隐患', value: '9', unit: '处', icon: 'ri-check-double-line', color: 'green' },
              { label: '巡检覆盖率', value: '97.4', unit: '%', icon: 'ri-percent-line', color: 'sky' },
            ].map((c) => (
              <div key={c.label} className="bg-gray-800/50 rounded-lg p-5 border border-teal-500/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-400">{c.label}</span>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-${c.color}-500/10`}>
                    <i className={`${c.icon} text-xl text-${c.color}-400`}></i>
                  </div>
                </div>
                <div className="text-3xl font-bold text-white">{c.value}<span className="text-sm font-normal text-gray-400 ml-1">{c.unit}</span></div>
              </div>
            ))}
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-line text-teal-400"></i>
              设施巡检完好率趋势（%）
            </h3>
            <div className="flex items-end gap-3 h-48">
              {mockDailyData.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs text-sky-400 font-medium">{d.facility}%</span>
                  <div className="w-full flex flex-col justify-end" style={{ height: '160px' }}>
                    <div
                      className="w-full bg-gradient-to-t from-sky-700 to-sky-400 rounded-t-md"
                      style={{ height: `${d.facility}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400">{d.date}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {[
              { title: '岸桥/场桥巡检', items: [
                { label: '巡检次数', value: '24 次' }, { label: '发现裂纹', value: '3 处' },
                { label: '螺栓松动', value: '5 处' }, { label: '已整改', value: '7 处' },
              ]},
              { title: '输油管道巡检', items: [
                { label: '巡检里程', value: '486 km' }, { label: '泄漏隐患', value: '2 处' },
                { label: '腐蚀点', value: '4 处' }, { label: '已整改', value: '5 处' },
              ]},
              { title: '高架桥巡检', items: [
                { label: '巡检次数', value: '18 次' }, { label: '结构异常', value: '1 处' },
                { label: '路面损坏', value: '3 处' }, { label: '已整改', value: '3 处' },
              ]},
              { title: '港口机械巡检', items: [
                { label: '巡检台次', value: '56 台次' }, { label: '机械故障', value: '2 处' },
                { label: '磨损预警', value: '6 处' }, { label: '已整改', value: '7 处' },
              ]},
            ].map((block) => (
              <div key={block.title} className="bg-gray-800/50 rounded-lg p-5 border border-teal-500/20">
                <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <i className="ri-tools-line text-teal-400"></i>{block.title}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {block.items.map((item) => (
                    <div key={item.label} className="bg-gray-700/30 rounded-lg p-3">
                      <div className="text-xs text-gray-400 mb-1">{item.label}</div>
                      <div className="text-base font-bold text-teal-400">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== 环境保护 Tab ===== */}
      {activeTab === 'environment' && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: '油污监测面积', value: '12,480', unit: '㎡', icon: 'ri-water-flash-line', color: 'teal' },
              { label: '粉尘超标次数', value: '18', unit: '次', icon: 'ri-haze-line', color: 'orange' },
              { label: '环保预警处置', value: '27', unit: '次', icon: 'ri-leaf-line', color: 'green' },
              { label: '环境达标率', value: '93.4', unit: '%', icon: 'ri-percent-line', color: 'sky' },
            ].map((c) => (
              <div key={c.label} className="bg-gray-800/50 rounded-lg p-5 border border-teal-500/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-400">{c.label}</span>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-${c.color}-500/10`}>
                    <i className={`${c.icon} text-xl text-${c.color}-400`}></i>
                  </div>
                </div>
                <div className="text-3xl font-bold text-white">{c.value}<span className="text-sm font-normal text-gray-400 ml-1">{c.unit}</span></div>
              </div>
            ))}
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
            <h3 className="text-base font-semibold text-white mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-line text-teal-400"></i>
              环境达标率趋势（%）
            </h3>
            <div className="flex items-end gap-3 h-48">
              {mockDailyData.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs text-green-400 font-medium">{d.environment}%</span>
                  <div className="w-full flex flex-col justify-end" style={{ height: '160px' }}>
                    <div
                      className="w-full bg-gradient-to-t from-green-800 to-green-500 rounded-t-md"
                      style={{ height: `${d.environment}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400">{d.date}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
              <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <i className="ri-water-flash-line text-teal-400"></i>海域油污监测统计
              </h4>
              <div className="space-y-3">
                {[
                  { label: '监测海域面积', value: '48,600 ㎡', color: 'teal' },
                  { label: '发现油污事件', value: '12 起', color: 'orange' },
                  { label: '最大污染面积', value: '380 ㎡', color: 'red' },
                  { label: '平均处置时间', value: '2.8 h', color: 'green' },
                  { label: '污染清除率', value: '91.7%', color: 'teal' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-700/40">
                    <span className="text-sm text-gray-400">{item.label}</span>
                    <span className={`text-sm font-semibold text-${item.color}-400`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-6 border border-teal-500/20">
              <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <i className="ri-haze-line text-orange-400"></i>粉尘排放监控统计
              </h4>
              <div className="space-y-3">
                {[
                  { label: '监测码头数量', value: '6 个', color: 'teal' },
                  { label: '粉尘超标次数', value: '18 次', color: 'orange' },
                  { label: '最高浓度记录', value: '320 μg/m³', color: 'red' },
                  { label: '湿法作业督促', value: '24 次', color: 'green' },
                  { label: '整改完成率', value: '95.8%', color: 'teal' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-700/40">
                    <span className="text-sm text-gray-400">{item.label}</span>
                    <span className={`text-sm font-semibold text-${item.color}-400`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
